import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';
import { useKiosk } from '../context/KioskContext';

// Import theme CSS
import '../styles/kiosk-theme.css';

/**
 * IdlePage - Attract screen for kiosk
 * 
 * Premium modern design with:
 * - Animated gradient background
 * - Restaurant branding
 * - Rotating promotions
 * - Large touch-to-start CTA
 */
export default function IdlePage() {
  const navigate = useNavigate();
  const { config, promotions, loading, resetSession, setView, cardReady } = useKiosk();
  const [currentPromoIndex, setCurrentPromoIndex] = useState(0);

  // Reset session when arriving at idle
  useEffect(() => {
    resetSession();
    setView('idle');
  }, [resetSession, setView]);

  // Rotate promotions every 5 seconds
  useEffect(() => {
    if (promotions.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentPromoIndex(prev => (prev + 1) % promotions.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [promotions.length]);

  const handleStartOrder = () => {
    navigate('start');
  };

  const restaurant = config?.restaurant || {};
  const currentPromo = promotions[currentPromoIndex];

  if (loading) {
    return (
      <div className="kiosk-container flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#f5a623] animate-spin mx-auto mb-4" />
          <p className="text-xl text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="kiosk-container relative overflow-hidden cursor-pointer"
      onClick={handleStartOrder}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleStartOrder()}
      aria-label="Touch anywhere to start your order"
    >
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0f1419] via-[#141a21] to-[#1a1f26]">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-[#f5a623]/[0.03] blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-[#ff6b35]/[0.03] blur-3xl transform -translate-x-1/2 translate-y-1/2" />
        
        {/* Subtle Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-between py-16 px-8">
        
        {/* Top Section - Restaurant Branding */}
        <div className="flex-1 flex flex-col items-center justify-center">
          {/* Logo */}
          {restaurant.logo_url ? (
            <div className="mb-8 animate-fade-in">
              <img 
                src={restaurant.logo_url} 
                alt={restaurant.name}
                className="w-32 h-32 rounded-3xl object-cover shadow-2xl shadow-black/50"
              />
            </div>
          ) : (
            <div className="mb-8 w-32 h-32 rounded-3xl bg-gradient-to-br from-[#f5a623] to-[#ff6b35] flex items-center justify-center shadow-2xl shadow-black/50 animate-fade-in">
              <span className="text-5xl font-bold text-[#0f1419]">
                {(restaurant.name || 'K')[0]}
              </span>
            </div>
          )}

          {/* Restaurant Name */}
          <h1 className="text-5xl md:text-6xl font-bold text-white text-center mb-4 font-['Outfit'] animate-fade-in">
            {restaurant.name || 'Welcome'}
          </h1>

          {/* Tagline */}
          <p className="text-xl text-gray-400 text-center max-w-md animate-fade-in">
            Fresh ingredients, made to order
          </p>
        </div>

        {/* Middle Section - Promotions */}
        {currentPromo && (
          <div className="w-full max-w-2xl mb-12 animate-fade-in">
            <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-r from-[#1a1f26] to-[#252b33] p-6">
              {/* Promo Content */}
              <div className="flex items-center gap-6">
                {currentPromo.image_url && (
                  <img 
                    src={currentPromo.image_url} 
                    alt={currentPromo.title}
                    className="w-24 h-24 rounded-xl object-cover flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#f5a623]/20 text-[#f5a623]">
                      TODAY'S SPECIAL
                    </span>
                  </div>
                  <h3 className="text-2xl font-semibold text-white truncate">
                    {currentPromo.title}
                  </h3>
                  {currentPromo.subtitle && (
                    <p className="text-lg text-gray-400 mt-1">
                      {currentPromo.subtitle}
                    </p>
                  )}
                </div>
              </div>

              {/* Promotion Dots */}
              {promotions.length > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  {promotions.map((_, idx) => (
                    <div
                      key={idx}
                      className={`
                        w-2 h-2 rounded-full transition-all duration-300
                        ${idx === currentPromoIndex 
                          ? 'bg-[#f5a623] w-6' 
                          : 'bg-white/30'
                        }
                      `}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bottom Section - CTA */}
        <div className="w-full max-w-md animate-slide-up">
          {/* Main CTA Button */}
          <button
            onClick={handleStartOrder}
            className="
              w-full h-20 
              flex items-center justify-center gap-4
              bg-gradient-to-r from-[#f5a623] to-[#ff6b35]
              text-[#0f1419] text-2xl font-bold
              rounded-2xl
              shadow-lg shadow-[#f5a623]/30
              transition-all duration-200
              hover:shadow-xl hover:shadow-[#f5a623]/40 hover:brightness-110
              active:scale-[0.98]
            "
            aria-label="Touch to start your order"
          >
            <span>Touch to Start Order</span>
            <ChevronRight size={28} />
          </button>

          {/* Pulsing Indicator */}
          <div className="flex items-center justify-center mt-6">
            <div className="relative">
              <div className="w-3 h-3 bg-[#f5a623] rounded-full animate-pulse" />
              <div className="absolute inset-0 w-3 h-3 bg-[#f5a623] rounded-full animate-ping" />
            </div>
          </div>

          {/* Helper Text */}
          <p className="text-center text-gray-500 mt-4 text-sm">
            Tap anywhere on screen to begin
          </p>
          {config && !cardReady && (
            <p className="text-center text-amber-300/90 mt-3 text-base font-medium" data-testid="idle-card-unavailable">
              Card reader unavailable — please see staff to pay
            </p>
          )}
        </div>
      </div>

      {/* Inline Animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out forwards;
        }
        .animate-slide-up {
          animation: slide-up 0.6s ease-out 0.3s forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
}
